export function isNewer(remote: string, local: string): boolean {
  const r = remote.split('.').map(Number)
  const l = local.split('.').map(Number)
  for (let i = 0; i < Math.max(r.length, l.length); i++) {
    const ri = r[i] ?? 0
    const li = l[i] ?? 0
    if (ri > li) return true
    if (ri < li) return false
  }
  return false
}

// Bundle versions are `<semver>.<run#>`; the run# moves on every push, the
// semver only at a release.
export function releaseOf(version: string): string {
  return version.split('.').slice(0, 3).join('.')
}

// A bundle that boots but dies before notifyAppReady() — an unset Firebase
// config is the way this happens (#368) — is rolled back by capgo inside a
// native process that cannot report back. From the web layer applyManifest()
// only ever *succeeds*, so nothing here learns the bundle is broken and every
// launch downloads and applies it again. Counting attempts across boots is the
// only signal available: if the same version keeps being applied and the device
// keeps waking up on the old bundle, it never activated.
export const MAX_APPLY_ATTEMPTS = 3

export type UpdateDecision =
  | { action: 'up_to_date' }
  | { action: 'capped' }
  | { action: 'prefetch'; reason: string }
  | { action: 'apply' }

// What a check does with a manifest. Swapping bundles restarts the app, which
// is jarring mid-use, so an ordinary push (a build-number-only bump) and a
// preview republish are only prefetched — the swap happens when the app next
// goes to the background. A new release (the semver moved) is the "everyone
// gets this now" lever and applies right away, unless an open dialog or a typed
// draft would be lost.
export function decideUpdate(input: {
  remote: string
  installed: string
  // Pinned preview channel: any version other than the running one counts as
  // newer — a channel's manifest always points at its latest publish, even if
  // the branch's package.json version moved backwards in between.
  preview: boolean
  attempts: number
  transientState: string | null
}): UpdateDecision {
  const { remote, installed, preview, attempts, transientState } = input
  const ahead = preview ? remote !== installed : isNewer(remote, installed)
  if (!ahead) return { action: 'up_to_date' }
  // Checked before prefetching too, so a bundle that never activates stops
  // being downloaded and a stuck preview still gets unpinned.
  if (attempts >= MAX_APPLY_ATTEMPTS) return { action: 'capped' }
  if (preview) return { action: 'prefetch', reason: 'preview' }
  if (!isNewer(releaseOf(remote), releaseOf(installed))) return { action: 'prefetch', reason: 'build_only' }
  if (transientState) return { action: 'prefetch', reason: transientState }
  return { action: 'apply' }
}
